import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import AuditInfo from 'components/audit/AuditInfo';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import GridItemList, { infoLabel, Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    ActiveChip,
    MetaItem,
    RECORD_GRID_CLASS,
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordMoreMenu,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import CustomFieldCards from 'pages/offices/CustomFieldCards';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetJobRoleByIdQuery } from 'store/api/jobRoles';
import { ClassifierData } from 'types/classifiers';
import { EntityType } from 'types/entity';
import { JobRoleDetailsDto } from 'types/jobRoles';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { JobRoleSection } from './form-sections/JobRoleSection';
import { translationRows } from './translationRows';
import { useDeleteJobRole } from './useDeleteJobRole';
import EmployeesView from './views/EmployeesView';

const USED_CLASSIFIERS = ['JOB_ROLE_GROUP_TYPE', 'LANGUAGE'];
const UPDATE_JOB_ROLES = ['UPDATE.JOB_ROLES'];
const DELETE_JOB_ROLES = ['DELETE.JOB_ROLES'];
const yesNo = (value: boolean) => (value ? t('yes') : t('no'));

/** The job role's read rows by name: the classic Overview lists them in one card, the new design by topic. */
const jobRoleRows = (jobRole: JobRoleDetailsDto, classifiers: Record<string, ClassifierData>) =>
    ({
        orderNum: { label: t('order-number'), content: jobRole.orderNum ?? '-' },
        name: { label: t('public-name'), content: jobRole.name || '-' },
        internalName: { label: t('job-roles.internal-name'), content: jobRole.internalName || '-' },
        abbreviation: { label: t('job-roles.abbreviation'), content: jobRole.abbreviation || '-' },
        category: {
            label: t('category'),
            content: getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], jobRole.groupNameTypeCode) || '-',
        },
        active: { label: t('employees.is-active'), content: yesNo(jobRole.isActive) },
        excludeFromMinmax: {
            label: t('job-roles.exclude-from-minmax-calculation'),
            content: yesNo(jobRole.excludeFromMinmaxCalculation),
        },
    }) satisfies Record<string, Row>;

const JobRolePage = (): JSX.Element => {
    const { id: jobRoleId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/job-roles');
    const isNew = useIsNewDesign();
    const canEdit = useHasPermissions(UPDATE_JOB_ROLES);
    const canDelete = useHasPermissions(DELETE_JOB_ROLES);
    const confirmDelete = useDeleteJobRole(jobRoleId);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: jobRole, isLoading: isLoadingJobRole } = useGetJobRoleByIdQuery(jobRoleId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!jobRole?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/job-roles');
    const draftActions = useDraftActions({
        draft: jobRole?.isDraft ? { type: 'job-role', id: jobRole.id } : null,
        createRoute: '/job-role/new',
        onDeleted: leaveDeleted,
    });
    const missingFields = draftActions.draft?.missingFields;
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({ codes: USED_CLASSIFIERS });
    const {
        customFields,
        customFieldsFiltered,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType: EntityType.JOB_ROLE, fields: jobRole?.customFields });

    const rows = useMemo(() => (jobRole ? jobRoleRows(jobRole, classifiers) : undefined), [jobRole, classifiers]);
    const generalRows: Row[] = useMemo(
        () =>
            rows
                ? withMissing(
                      [
                          rows.orderNum,
                          rows.name,
                          rows.internalName,
                          rows.abbreviation,
                          rows.category,
                          rows.active,
                          rows.excludeFromMinmax,
                          ...customFieldsFiltered.map((field) => ({
                              label: field.label ? (field.shouldTranslate ? t(field.label) : field.label) : '',
                              content: <CustomFieldDisplay field={field} classifiers={allClassifiers} />,
                          })),
                      ],
                      missingFields
                  )
                : [],
        [rows, missingFields, customFieldsFiltered, allClassifiers]
    );

    const tabs = useMemo<TabType[]>(() => {
        if (!jobRole || !rows) return [];
        const auditInfo = (
            <AuditInfo
                createdAt={jobRole.createdAt}
                createdByEmployee={jobRole.createdByEmployee}
                lastModifiedAt={jobRole.lastModifiedAt}
                changedByEmployee={jobRole.changedByEmployee}
            />
        );
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: isNew ? (
                    <div className={RECORD_GRID_CLASS}>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            <JobRoleSection id="job-role" titleId="job-roles.job-role" jobRole={jobRole}>
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="job-roles.job-role"
                                        // Under the fields' labels and info icons, so each wraps as its field's.
                                        data={withMissing(
                                            [
                                                {
                                                    ...rows.orderNum,
                                                    label: infoLabel(
                                                        'order-number',
                                                        'job-roles.tooltips.order-number',
                                                        'justify-between'
                                                    ),
                                                },
                                                rows.name,
                                                rows.internalName,
                                                rows.abbreviation,
                                                {
                                                    ...rows.category,
                                                    label: infoLabel(
                                                        'category',
                                                        'job-roles.tooltips.category',
                                                        'justify-between'
                                                    ),
                                                },
                                            ],
                                            missingFields
                                        )}
                                        button={edit}
                                    />
                                )}
                            </JobRoleSection>
                            <JobRoleSection
                                id="translations"
                                titleId="job-roles.public-name-translations"
                                jobRole={jobRole}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="job-roles.public-name-translations"
                                        data={translationRows(classifiers, jobRole.entityTexts)}
                                        button={edit}
                                    />
                                )}
                            </JobRoleSection>
                            {customFieldsFiltered.length > 0 ? (
                                <JobRoleSection id="custom-fields" titleId="custom-fields.title" jobRole={jobRole}>
                                    {(edit) => (
                                        <CustomFieldCards
                                            fields={customFields}
                                            classifiers={allClassifiers}
                                            edit={edit}
                                        />
                                    )}
                                </JobRoleSection>
                            ) : null}
                            {auditInfo}
                        </div>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {isDraft ? <DraftSharedWith /> : null}
                            <JobRoleSection id="settings" titleId="job-roles.settings" jobRole={jobRole}>
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="job-roles.settings"
                                        data={[rows.active, rows.excludeFromMinmax]}
                                        button={edit}
                                    />
                                )}
                            </JobRoleSection>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <GridItemList titleId="matters.overview" data={generalRows} />
                        {isDraft ? <DraftSharedWith /> : null}
                        {auditInfo}
                    </div>
                ),
            },
            {
                id: 'employees',
                label: t('employees.employees'),
                // Nobody holds a draft role.
                hidden: isDraft,
                component: <EmployeesView jobRoleEmployees={jobRole.jobRoleEmployees} />,
            },
        ];
    }, [
        jobRole,
        rows,
        generalRows,
        classifiers,
        allClassifiers,
        customFields,
        customFieldsFiltered,
        isNew,
        isDraft,
        missingFields,
    ]);

    if (isLoadingJobRole || isLoadingClassifiers || isLoadingCustomFieldData) {
        return <Loader />;
    }

    if (!jobRole) {
        return <></>;
    }

    const hasEmployees = !!jobRole.jobRoleEmployees?.length;
    const tabsGallery = (
        <TabsGallery
            tabs={tabs}
            tabGroupId="job-role-page"
            trackTabsInHistoryState
            usePathBasedRouting
            wrapperClassName={RECORD_TABS_CLASS}
            panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
            className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
        />
    );

    const draft = draftActions.draft;
    const title = jobRole.name || (isDraft ? t('new-entity.job-role') : '-');
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    if (isNew) {
        const category = getClassifierText(classifiers['JOB_ROLE_GROUP_TYPE'], jobRole.groupNameTypeCode);
        const header = (
            <RecordHeader
                number={jobRole.abbreviation}
                chips={
                    <>
                        {isDraft ? <DraftChip /> : null}
                        {category ? <StatusChip type="neutral" label={category} /> : null}
                        <ActiveChip isActive={jobRole.isActive} />
                    </>
                }
                title={title}
                pin={isDraft ? undefined : { kind: 'job-role', id: jobRole.id }}
                meta={
                    <>
                        {jobRole.internalName ? (
                            <MetaItem label={<FormattedMessage id="job-roles.internal-name" />}>
                                {jobRole.internalName}
                            </MetaItem>
                        ) : null}
                        {jobRole.orderNum != null ? (
                            <MetaItem label={<FormattedMessage id="order-number" />}>{jobRole.orderNum}</MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={
                    isDraft ? (
                        <DraftRecordActions actions={draftActions} />
                    ) : (
                        // Delete moves here from the edit form, with its gate and its block while colleagues
                        // hold the role.
                        <RecordMoreMenu
                            menuItems={
                                canDelete
                                    ? [
                                          {
                                              handler: confirmDelete,
                                              title: t('delete'),
                                              variant: 'destructive',
                                              disabled: hasEmployees,
                                              subtitle: hasEmployees
                                                  ? t('job-roles.delete-restriction-text')
                                                  : undefined,
                                          },
                                      ]
                                    : []
                            }
                        />
                    )
                }
            />
        );
        return (
            <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
                <RecordPage
                    header={
                        draftNotice ? (
                            <div className="flex flex-col gap-4">
                                {header}
                                {draftNotice}
                            </div>
                        ) : (
                            header
                        )
                    }
                >
                    <SectionEditProvider canEdit={canEdit && !isDraft}>{tabsGallery}</SectionEditProvider>
                </RecordPage>
                {draftActions.shareDialog}
            </DraftActionsContext.Provider>
        );
    }

    return (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <PaddedLayout className="flex h-full flex-col overflow-hidden">
                <PageHeader
                    title={title}
                    showBreadcrumb
                    onBack={goBack}
                    buttons={
                        isDraft ? (
                            <div className="flex items-center justify-end gap-2">
                                <DraftRecordActions actions={draftActions} />
                            </div>
                        ) : (
                            <PermissionButton
                                permissions={UPDATE_JOB_ROLES}
                                variant="outlined"
                                size="small"
                                onClick={() => navigateWithReturn(`/job-role/${jobRoleId}/edit`)}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        )
                    }
                >
                    {draft ? (
                        <div className="flex flex-wrap items-center gap-2 py-3">
                            <DraftChip />
                            <DraftMeta draft={draft} />
                        </div>
                    ) : null}
                </PageHeader>
                {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                {tabsGallery}
            </PaddedLayout>
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );
};

export default JobRolePage;
